// ========================================
// 📁 src/contexts/NotificationContext.js - VERSION OPTIMISÉE
// ========================================

import React, { createContext, useContext, useState, useCallback } from 'react';

// Contexte pour les notifications
const NotificationContext = createContext();

// Hook pour utiliser les notifications depuis n'importe quel composant
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications doit être utilisé dans un NotificationProvider');
  }
  return context;
};

// Composant Toast Notification - MÉMORISÉ
const ToastNotification = React.memo(({ notification, onClose }) => {
  React.useEffect(() => {
    const timer = setTimeout(() => {
      onClose(notification.id);
    }, notification.duration || 5000);

    return () => clearTimeout(timer);
  }, [notification.id, notification.duration, onClose]); // ✅ Dépendances optimisées

  const getIcon = (type) => {
    switch (type) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'warning': return '⚠️';
      case 'info': return 'ℹ️';
      case 'conflict': return '🚫';
      case 'collaboration': return '👥';
      case 'dragdrop': return '🔄';
      default: return 'ℹ️';
    }
  };

  const getColors = (type) => {
    switch (type) {
      case 'success': return { bg: '#d4edda', border: '#c3e6cb', text: '#155724' };
      case 'error': return { bg: '#f8d7da', border: '#f5c6cb', text: '#721c24' };
      case 'warning': return { bg: '#fff3cd', border: '#ffeaa7', text: '#856404' };
      case 'info': return { bg: '#cce7ff', border: '#99d6ff', text: '#004085' };
      case 'conflict': return { bg: '#ffe6e6', border: '#ffcccc', text: '#cc0000' };
      case 'collaboration': return { bg: '#e7f3ff', border: '#b3d9ff', text: '#0c5aa6' };
      case 'dragdrop': return { bg: '#f0f8f0', border: '#d0f0d0', text: '#2d5a2d' };
      default: return { bg: '#e2e3e5', border: '#d6d8db', text: '#383d41' };
    }
  };

  const colors = getColors(notification.type);

  return (
    <div
      style={{
        background: colors.bg,
        border: `2px solid ${colors.border}`,
        color: colors.text,
        padding: '16px',
        borderRadius: '8px',
        marginBottom: '12px',
        minWidth: '320px',
        maxWidth: '480px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        animation: 'slideInRight 0.3s ease-out',
        position: 'relative',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px'
      }}
    >
      <div style={{ fontSize: '24px', flexShrink: 0 }}>
        {getIcon(notification.type)}
      </div>
      
      <div style={{ flex: 1 }}>
        {notification.title && (
          <div style={{ 
            fontWeight: 'bold', 
            fontSize: '16px', 
            marginBottom: '4px' 
          }}>
            {notification.title}
          </div>
        )}
        <div style={{ fontSize: '14px', lineHeight: '1.4' }}>
          {notification.message}
        </div>
        {notification.details && (
          <div style={{ 
            fontSize: '12px', 
            marginTop: '8px', 
            opacity: 0.8,
            fontStyle: 'italic'
          }}>
            {notification.details}
          </div>
        )}
        {notification.actions && (
          <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
            {notification.actions.map((action, index) => (
              <button
                key={index}
                onClick={() => {
                  action.onClick();
                  if (action.closeAfter !== false) {
                    onClose(notification.id);
                  }
                }}
                style={{
                  padding: '6px 12px',
                  background: action.primary ? colors.border : 'transparent',
                  border: `1px solid ${colors.border}`,
                  borderRadius: '4px',
                  fontSize: '12px',
                  cursor: 'pointer',
                  color: colors.text
                }}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>
      
      <button
        onClick={() => onClose(notification.id)}
        style={{
          background: 'transparent',
          border: 'none',
          fontSize: '18px',
          cursor: 'pointer',
          padding: '0',
          color: colors.text,
          opacity: 0.7,
          flexShrink: 0
        }}
        title="Fermer"
      >
        ✕
      </button>
      
      <div
        style={{
          position: 'absolute',
          bottom: '0',
          left: '0',
          height: '3px',
          background: colors.border,
          borderRadius: '0 0 8px 8px',
          animation: `progressBar ${notification.duration || 5000}ms linear forwards`
        }}
      />
    </div>
  );
});

// Conteneur des notifications - MÉMORISÉ
const ToastContainer = React.memo(({ notifications, onClose }) => {
  return (
    <>
      <style>
        {`
          @keyframes slideInRight {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
          
          @keyframes progressBar {
            from { width: 100%; }
            to { width: 0%; }
          }
        `}
      </style>
      
      <div
        style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end'
        }}
      >
        {notifications.map(notification => (
          <ToastNotification
            key={notification.id}
            notification={notification}
            onClose={onClose}
          />
        ))}
      </div>
    </>
  );
});

// Provider pour les notifications - OPTIMISÉ
export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  // ✅ TOUTES LES FONCTIONS MÉMORISÉES avec useCallback
  const addNotification = useCallback((notification) => {
    const id = Date.now() + Math.random();
    const newNotification = {
      id,
      type: 'info',
      duration: 5000,
      ...notification
    };
    
    setNotifications(prev => [...prev, newNotification]);
    return id;
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  // ✅ Fonctions helper mémorisées
  const showSuccess = useCallback((message, options = {}) => {
    return addNotification({
      type: 'success',
      message,
      ...options
    });
  }, [addNotification]);

  const showError = useCallback((message, options = {}) => {
    return addNotification({
      type: 'error',
      message,
      duration: 7000,
      ...options
    });
  }, [addNotification]);

  const showWarning = useCallback((message, options = {}) => {
    return addNotification({
      type: 'warning',
      message,
      ...options
    });
  }, [addNotification]);

  const showInfo = useCallback((message, options = {}) => {
    return addNotification({
      type: 'info',
      message,
      ...options
    });
  }, [addNotification]);

  const showConflict = useCallback((message, options = {}) => {
    return addNotification({
      type: 'conflict',
      message,
      duration: 8000,
      ...options
    });
  }, [addNotification]);

  const showCollaboration = useCallback((message, options = {}) => {
    return addNotification({
      type: 'collaboration',
      message,
      duration: 4000,
      ...options
    });
  }, [addNotification]);

  const showDragDrop = useCallback((message, options = {}) => {
    return addNotification({
      type: 'dragdrop',
      message,
      duration: 3000,
      ...options
    });
  }, [addNotification]);

  // ✅ Fonction avec confirmation intégrée - mémorisée
  const showConfirmation = useCallback((message, options = {}) => {
    return new Promise((resolve) => {
      const confirmId = addNotification({
        type: 'warning',
        message,
        duration: 0, // Pas d'auto-suppression
        actions: [
          {
            label: 'Annuler',
            onClick: () => resolve(false)
          },
          {
            label: 'Confirmer',
            primary: true,
            onClick: () => resolve(true)
          }
        ],
        ...options
      });
    });
  }, [addNotification]);

  // ✅ OBJET VALUE MÉMORISÉ pour éviter les re-renders
  const value = React.useMemo(() => ({
    notifications,
    addNotification,
    removeNotification,
    clearAll,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showConflict,
    showCollaboration,
    showDragDrop,
    showConfirmation
  }), [
    notifications,
    addNotification,
    removeNotification,
    clearAll,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showConflict,
    showCollaboration,
    showDragDrop,
    showConfirmation
  ]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <ToastContainer
        notifications={notifications}
        onClose={removeNotification}
      />
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;